/**
 * useTrackWatchSession — drives the /streams/view-event heartbeat lifecycle for
 * one watch session.
 *
 * `isWatching` is the accessible engagement signal computed by the caller
 * (deliberate intent + video PLAYING + stream live, treated as idle only when
 * the user is engaging via NEITHER sight nor sound). This hook does not decide
 * engagement — it just turns the signal into start → heartbeat… → end calls,
 * with server-authoritative timing on the backend.
 *
 * - first `isWatching === true`  → POST start, capture viewId, begin heartbeats
 * - while true                   → POST heartbeat every HEARTBEAT_INTERVAL_MS
 * - `isWatching === false`       → POST end
 * - unmount / page unload        → POST end via keepalive fetch (only reliable
 *   delivery path on unload; the generated client can't be used there)
 */
import { useCallback, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import {
    StreamingApi,
    StreamViewEventBodyActionEnum,
    StreamViewEventBodyPlatformEnum,
    Token,
} from "@devour/client";
import getConfig from "@/utils/getConfig";

/** Heartbeat cadence. The server caps each tick's credit, so this trades accuracy vs request volume, not correctness. */
const HEARTBEAT_INTERVAL_MS = 30_000;

export interface WatchSessionContext {
    /**
     * The generated enum, not a hand-written union — a local copy would drift
     * silently the day the API gains a fourth platform, and the drift would
     * only surface as a cast at the call site.
     */
    platform: StreamViewEventBodyPlatformEnum;
    channelId: string;
    surfaceId?: string;
    competitionId?: string;
}

export function useTrackWatchSession(isWatching: boolean, context: WatchSessionContext | undefined): void {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const tokenRef = useRef<Token | undefined>(fullToken ?? undefined);
    const contextRef = useRef<WatchSessionContext | undefined>(context);
    const viewIdRef = useRef<string | null>(null);
    const startingRef = useRef(false);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        tokenRef.current = fullToken ?? undefined;
    }, [fullToken?.token]);
    useEffect(() => {
        contextRef.current = context;
    }, [context?.platform, context?.channelId, context?.surfaceId, context?.competitionId]);

    const stopHeartbeat = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    const sendHeartbeat = useCallback(async () => {
        const viewId = viewIdRef.current;
        if (!viewId || !tokenRef.current?.token) return;
        try {
            await new StreamingApi(getConfig(tokenRef.current)).createStreamsViewEvent({
                streamViewEventBody: { action: StreamViewEventBodyActionEnum.heartbeat, viewId },
            });
        } catch {
            // best-effort — a dropped heartbeat costs at most one interval of credit
        }
    }, []);

    /** End via keepalive fetch — the only reliable delivery on unmount / page unload. */
    const endViaKeepalive = useCallback(() => {
        const viewId = viewIdRef.current;
        const token = tokenRef.current;
        viewIdRef.current = null;
        stopHeartbeat();
        if (!viewId || !token?.token) return;
        const config = getConfig(token);
        window.fetch(`${config.basePath}/streams/view-event`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(config.headers as Record<string, string>),
            },
            body: JSON.stringify({ action: "end", viewId }),
            keepalive: true,
        }).catch(() => {});
    }, [stopHeartbeat]);

    // Start / end the session as the accessible watching signal flips.
    useEffect(() => {
        let cancelled = false;

        const endSession = async () => {
            const viewId = viewIdRef.current;
            viewIdRef.current = null;
            stopHeartbeat();
            if (!viewId || !tokenRef.current?.token) return;
            try {
                await new StreamingApi(getConfig(tokenRef.current)).createStreamsViewEvent({
                    streamViewEventBody: { action: StreamViewEventBodyActionEnum.end, viewId },
                });
            } catch {
                // best-effort — the backend reaper closes an un-ended session
            }
        };

        const start = async () => {
            const ctx = contextRef.current;
            if (viewIdRef.current || startingRef.current || !ctx || !tokenRef.current?.token) return;
            startingRef.current = true;
            try {
                const data = await new StreamingApi(getConfig(tokenRef.current)).createStreamsViewEvent({
                    streamViewEventBody: {
                        action: StreamViewEventBodyActionEnum.start,
                        platform: ctx.platform,
                        channelId: ctx.channelId,
                        surfaceId: ctx.surfaceId,
                        competitionId: ctx.competitionId,
                    },
                });
                viewIdRef.current = data.viewId || null;
                if (cancelled || !isWatching) {
                    // Watching stopped while the start was in flight — close it out.
                    void endSession();
                    return;
                }
                if (viewIdRef.current) {
                    stopHeartbeat();
                    intervalRef.current = setInterval(() => {
                        void sendHeartbeat();
                    }, HEARTBEAT_INTERVAL_MS);
                }
            } catch {
                // start failed — no session; a later isWatching toggle retries
            } finally {
                startingRef.current = false;
            }
        };

        if (isWatching) {
            void start();
        } else {
            void endSession();
        }

        return () => {
            cancelled = true;
        };
    }, [isWatching, sendHeartbeat, stopHeartbeat]);

    // Close the session on unmount + page unload (keepalive delivery).
    useEffect(() => {
        window.addEventListener("pagehide", endViaKeepalive);
        window.addEventListener("beforeunload", endViaKeepalive);
        return () => {
            window.removeEventListener("pagehide", endViaKeepalive);
            window.removeEventListener("beforeunload", endViaKeepalive);
            endViaKeepalive();
        };
    }, [endViaKeepalive]);
}
